import { Component, Input } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import * as Highcharts from 'highcharts';
import { reportChartBarOptions } from '../highcharts-options';
import { QuickBuildService, UIFilter } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import { SearchFilter } from 'ldt-people-api';
import { TenureCurrentBreakdownConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const FIELDS_TO_GROUP_BY = [
  'jobs.function',
  'jobs.level',
  'jobs.metadata.previous_job.company.name',
  'education.school',
];

@Component({
    selector: 'app-reports-tenure-current-breakdown',
    templateUrl: './tenure-current-breakdown.component.html',
    styleUrls: ['./tenure-current-breakdown.component.scss'],
    standalone: false
})
export class TenureCurrentBreakdownComponent extends BaseReportComponent<TenureCurrentBreakdownConfig> {
  @Input() boxShadow: boolean = true;
  @Input() maxSeries: number = 20;

  // We want to accept an array of fields here, but we need to change them to ToggleOptions
  @Input()
  set breakdownByOptions(fields: string[]) {
    this._breakdownByOptions = fields || FIELDS_TO_GROUP_BY;
    this.breakdownByToggleOptions = this._breakdownByOptions.map((field) => ({
      value: field,
      displayText: this.getFieldDisplayName(field),
    }));
  }
  get breakdownByOptions(): string[] {
    return this._breakdownByOptions;
  }
  private _breakdownByOptions: string[] = FIELDS_TO_GROUP_BY;

  breakdownByToggleOptions: ToggleOption[] = [];

  @Input()
  set selectedBreakdownBy(value: string | undefined) {
    if (value) {
      this._selectedBreakdownBy = value;
    }
  }
  get selectedBreakdownBy(): string {
    return this._selectedBreakdownBy;
  }

  private _selectedBreakdownBy: string = FIELDS_TO_GROUP_BY[0];

  protected readonly CHART_ID = 'tenure';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedBreakdownBy', () => this._selectedBreakdownBy);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartBarOptions)
    );
    this.chart.update(
      {
        title: { text: 'Active Employee Tenure' },
        yAxis: { title: { text: 'Average Tenure (months)' } },
      },
      true,
      true
    );
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    if (!this.quickBuild) return;
    // Active tenure doesn't support job_role filters
    const fGroup = QuickBuildService.findFilterGroupById(
      'moneyball',
      this.quickBuild?.rootFilterGroup
    );
    if (
      fGroup?.filters.some((f) => QuickBuildService.isUIFilter(f) && f.filter.field === 'job_role')
    ) {
      this.showChartError(undefined, "Active tenure doesn't support role filters");
      return;
    }

    this.showLoading();

    const reportParams = {
      group_by: [this._selectedBreakdownBy],
    };
    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        this.CHART_ID,
        reportParams,
        this.reportTarget,
        this.quickBuild
      )
      .subscribe({
        next: (data) => {
          Log.d('TenureCurrentBreakdownComponent data', data);
          const [series, categories] = this.generateChartSeries(
            data.count,
            data.data.tenure,
            this._selectedBreakdownBy
          );
          this.updateChart(series, categories);
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('TenureCurrentBreakdownComponent getChartData error', error);
          this.showChartError(error);
        },
      });
    this.subscriptions.add(sub);
  }

  protected generateChartSeries(total: number, data: any, key: string): [any[], any[]] {
    Log.d('TenureCurrentBreakdownComponent generateChartSeries', total, data);
    if (!data) {
      return [[], []];
    }

    // Filter out the current company
    const filteredData = data.filter((c: any) => {
      if (
        key === 'jobs.metadata.previous_job.company.name' &&
        c.group_values[0]?.value === this.quickBuild?.name
      ) {
        return false;
      }
      return true;
    });

    const seriesData = filteredData.slice(0, this.maxSeries).map((c: any) => {
      return c.avg_tenure;
    });

    const categories = filteredData.slice(0, this.maxSeries).map((c: any) => {
      return c.group_values[0]?.value;
    });

    return [
      [
        {
          id: key,
          name: key,
          showInLegend: false,
          dataLabels: {
            format: '{point.y:.0f}',
          },
          data: seriesData,
          type: 'bar',
        },
      ],
      categories,
    ];
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const series = point.series;
    const category = point.category as string;
    if (!this.quickBuild) {
      Log.s('TenureCurrentBreakdownComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
      });
      return [];
    }

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);

    // Fix for weird previous company situation
    let breakdownBy = this._selectedBreakdownBy;
    if (breakdownBy === 'jobs.metadata.previous_job.company.name') {
      breakdownBy = 'jobs.previous_job.company.name';
    }

    const categoryFilter: UIFilter = {
      filter: {
        field: breakdownBy,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [category],
      },
    };
    Log.d('TenureCurrentBreakdownComponent getContextMenuItems categoryFilter', categoryFilter);

    if (breakdownBy.startsWith('jobs')) {
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], 'moneyball');
    } else {
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], '__root__');
    }

    return [
      {
        label: `View ${category} employees that work at ${series.name}`,
        icon: 'group',
        action: (p) => {
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.jobsGroupType = 'active';
          qbCopy.name = `${category} employees at ${series.name}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  onBreakdownByChanged(value: string) {
    if (!value) return;

    this._selectedBreakdownBy = value;
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }
}
